import { useCallback, useEffect, useRef, useState } from 'react';
import { loadExtractData } from './extract/extractData';
import type { ExtractData } from './extract/extractData';

/**
 * useWorkflowData: loads the loans, files and documents of a DD/BA job once for
 * the whole Workspace. The Rail (same in every Mode) and the Extract body read
 * it from the Workspace context instead of loading it again.
 *
 * - The first load shows a loading state; later refreshes are quiet and keep
 *   what is on screen if they fail.
 * - `refreshKey` changes (the job's progress, the Mode) trigger a quiet refresh.
 * - `refresh()` is for bodies that change the data (loans added in Intake).
 *
 * Requirements: 3.1, 3.4.
 */
export type WorkflowDataState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; data: ExtractData };

export interface UseWorkflowDataResult {
  state: WorkflowDataState;
  /** Re-fetch. `showSpinner` puts the Rail and Extract back into the loading state. */
  refresh: (showSpinner?: boolean) => Promise<void>;
}

export function useWorkflowData(
  jobId: string,
  enabled: boolean,
  refreshKey: string
): UseWorkflowDataResult {
  const [state, setState] = useState<WorkflowDataState>({ status: 'loading' });
  const requestId = useRef(0);
  const loaded = useRef(false);
  const loadedJobId = useRef(jobId);

  const refresh = useCallback(
    async (showSpinner = false) => {
      const id = ++requestId.current;
      if (showSpinner) setState({ status: 'loading' });
      try {
        const data = await loadExtractData(jobId);
        if (id === requestId.current) setState({ status: 'ready', data });
      } catch (err) {
        console.error('[Workspace] Failed to load the loans and documents:', err);
        // A failed refresh keeps what is already on screen.
        if (id === requestId.current) {
          setState((prev) => (prev.status === 'ready' && !showSpinner ? prev : { status: 'error' }));
        }
      }
    },
    [jobId]
  );

  useEffect(() => {
    if (!enabled) return;
    const jobChanged = loadedJobId.current !== jobId;
    loadedJobId.current = jobId;
    void refresh(jobChanged || !loaded.current);
    loaded.current = true;
  }, [refresh, jobId, enabled, refreshKey]);

  // A response that arrives after unmount is dropped.
  useEffect(
    () => () => {
      requestId.current += 1;
    },
    []
  );

  return { state, refresh };
}

export default useWorkflowData;
